import React, { useMemo } from 'react';
import BarrasHorizontais from '../shared/BarrasHorizontais';
import {
  agruparPorTouro, resumoDoSemen, totaisDoLivro, emDoses, dataCurta,
} from '../../../data/semen';
import { enderecoDoBotijao } from '../../../data/estoque';
import { Destaque, Indicador, Cartao, Tabela, Vazio, num, BotaoPDF } from './Pecas';
import { gerarRelatorioPDF, descreverFiltros } from '../../../utils/relatorioPDF';

/**
 * Relatório de sêmen: o que está no botijão agora, e como se chegou nele.
 *
 * É O ÚNICO PAINEL SEM PERÍODO, e isso é a decisão central.
 *
 *   Saldo é um RETRATO, não um intervalo. "Quantas doses de CABARÉ eu tenho
 *   entre março e junho" não é pergunta que tenha resposta — ou a palheta está
 *   no botijão hoje, ou não está. Recortar por data daria um número que parece
 *   saldo e não é de ninguém: nem o de antes, nem o de agora.
 *
 *   Por isso `FILTROS_POR_ABA` desliga as datas nesta aba, e o cabeçalho do
 *   PDF não imprime período. Relatório que mente no cabeçalho é pior do que
 *   relatório nenhum — e "período: 01/03 a 30/06" sobre um saldo de hoje seria
 *   exatamente isso.
 *
 * A MÉTRICA QUE LIDERA É A DOSE, não o dinheiro nem a contagem de partidas: a
 * pergunta que traz alguém aqui é "quanto sêmen eu tenho", e a partida é só
 * como esse estoque está dividido em lotes.
 *
 * As quatro parcelas do topo FECHAM: entradas − saídas − usadas = saldo. Quem
 * recebe a folha consegue somar e conferir, em vez de ter que acreditar.
 */
const PainelSemen = ({ linhas, dimensao, contexto = {} }) => {
  const resumo = useMemo(() => resumoDoSemen(linhas), [linhas]);
  const livro  = useMemo(() => totaisDoLivro(linhas), [linhas]);
  const porTouro = useMemo(() => agruparPorTouro(linhas), [linhas]);

  const porDimensao = useMemo(() => {
    const mapa = new Map();
    linhas.forEach(l => {
      // `dono` é nulo quando a palheta é do laboratório — e "do laboratório" é
      // uma resposta, não um buraco. Um travessão aqui faria o estoque próprio
      // parecer cadastro incompleto.
      const bruto = dimensao === 'dono' ? (l.dono || 'Do laboratório') : l[dimensao];
      const chave = bruto || '—';
      const a = mapa.get(chave) || { chave, saldo: 0, partidas: 0 };
      a.saldo += Number(l.saldo_doses) || 0;
      a.partidas += 1;
      mapa.set(chave, a);
    });
    return [...mapa.values()].sort((a, b) => b.saldo - a.saldo);
  }, [linhas, dimensao]);

  const COLUNAS = [
    { chave: 'touro', rotulo: 'Touro', valor: (l) => l.touro || '—' },
    { chave: 'lote', rotulo: 'Lote', valor: (l) => l.codigo || '—' },
    { chave: 'dono', rotulo: 'Dono', valor: (l) => l.dono || 'Do laboratório' },
    { chave: 'forn', rotulo: 'Fornecedor', valor: (l) => l.fornecedor || '—' },
    { chave: 'ent', rotulo: 'Entrada', valor: (l) => dataCurta(l.data_entrada) || '—' },
    { chave: 'de', rotulo: 'Entradas', numerica: true, valor: (l) => emDoses(l.doses_entradas) },
    { chave: 'us', rotulo: 'Usadas', numerica: true,
      valor: (l) => (Number(l.doses_usadas) ? emDoses(l.doses_usadas) : '—') },
    { chave: 'sa', rotulo: 'Saídas', numerica: true,
      valor: (l) => (Number(l.doses_saidas) ? emDoses(l.doses_saidas) : '—') },
    { chave: 'saldo', rotulo: 'Saldo', numerica: true, forte: true,
      valor: (l) => emDoses(l.saldo_doses) },
    { chave: 'onde', rotulo: 'Onde está', valor: (l) => enderecoDoBotijao(l) || '—' },
  ];

  // O PDF recebe os MESMOS objetos que a tela — `resumo` e `livro` são os
  // mesmos useMemo dos cartões acima, e `COLUNAS` é a mesma especificação da
  // tabela. É a única defesa que funciona contra o papel discordar da tela.
  const imprimir = () => gerarRelatorioPDF({
    titulo: 'Relatório de estoque de sêmen',
    laboratorio: contexto.laboratorio,
    // `contexto.periodo` chega falso nesta aba: o saldo é de hoje, e datar um
    // retrato com um intervalo faria a folha afirmar algo que ela não mede.
    filtros: descreverFiltros(contexto),
    indicadores: [
      { rotulo: 'Doses em estoque', valor: emDoses(livro.saldo),
        detalhe: `${num(resumo.touros)} touro(s) · ${num(resumo.partidas)} partida(s)` },
      { rotulo: 'Entradas', valor: emDoses(livro.entradas), detalhe: 'tudo que já entrou' },
      { rotulo: 'Usadas em FIV', valor: emDoses(livro.usadas), detalhe: 'descongeladas na bancada' },
      { rotulo: 'Saídas', valor: emDoses(livro.saidas), detalhe: 'venda, envio, perda, descarte' },
    ],
    grupos: {
      titulo: 'Por touro',
      colunas: [
        { chave: 't', rotulo: 'Touro', valor: (g) => g.touro },
        { chave: 'p', rotulo: 'Partidas', numerica: true, valor: (g) => num(g.partidas.length) },
        { chave: 's', rotulo: 'Doses', numerica: true, forte: true, valor: (g) => emDoses(g.saldo) },
      ],
      linhas: porTouro,
    },
    detalhe: { titulo: 'Partidas', colunas: COLUNAS, linhas },
  });

  if (linhas.length === 0) {
    return <Vazio mensagem="Nenhuma partida de sêmen cadastrada para este filtro." />;
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Destaque rotulo="Doses em estoque" valor={emDoses(livro.saldo)}
          detalhe={`${num(resumo.touros)} touro(s) · ${num(resumo.partidas)} partida(s)`} />
        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Indicador rotulo="Entradas" valor={emDoses(livro.entradas)}
            detalhe="tudo que já entrou" />
          <Indicador rotulo="Usadas em FIV" valor={emDoses(livro.usadas)}
            detalhe="descongeladas na bancada" />
          <Indicador rotulo="Saídas" valor={emDoses(livro.saidas)}
            detalhe="venda, envio, perda, descarte" />
        </div>
      </div>

      {/* Dito, e não escondido: a partida zerada continua na lista, e sem esta
          linha o "12 partidas" do topo não bate com o que se vê no botijão. */}
      {resumo.zeradas > 0 && (
        <p className="text-xs text-slate-500">
          {resumo.zeradas === 1
            ? '1 partida está zerada e continua na lista'
            : `${num(resumo.zeradas)} partidas estão zeradas e continuam na lista`}
          {' — '}saldo zero é história, não ausência.
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Cartao titulo="Doses por touro"
          subtitulo="É como a pergunta é feita na bancada — ninguém pergunta por lote.">
          <BarrasHorizontais
            dados={porTouro.slice(0, 12).map(g => ({
              rotulo: g.touro, valor: g.saldo, grupo: g,
            }))}
            detalhe={(d) => `${emDoses(d.grupo.saldo)} dose(s) · ${num(d.grupo.partidas.length)} partida(s)`}
          />
        </Cartao>

        <Cartao titulo="Doses por dono"
          subtitulo="Muda com o agrupamento escolhido acima.">
          <BarrasHorizontais
            dados={porDimensao.slice(0, 12).map(g => ({
              rotulo: g.chave, valor: g.saldo, grupo: g,
            }))}
            detalhe={(d) => `${emDoses(d.grupo.saldo)} dose(s) · ${num(d.grupo.partidas)} partida(s)`}
          />
        </Cartao>
      </div>

      <Tabela
        titulo="Partidas"
        subtitulo={`${num(linhas.length)} no estoque`}
        colunas={COLUNAS}
        linhas={linhas.map(l => ({ ...l, chave: l.semen_batch_id }))}
        acao={<BotaoPDF onClick={imprimir} />}
      />
    </div>
  );
};

export default PainelSemen;
